import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Typography,
  Grid,
  Paper,
  Box,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Breadcrumbs,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Chip,
  InputAdornment
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { oneTimeFeeAPI } from '../../services/api';
import { OneTimeFee } from '../../types';
import ErrorBoundary from '../common/ErrorBoundary';

interface FeeFormData {
  feeType: 'initiation' | 'activation' | 'equipment' | 'locker_rental' | 'personal_training' | 'late_fee' | 'cancellation' | 'other';
  feeName: string;
  amount: number;
}

const initialFormData: FeeFormData = {
  feeType: 'initiation',
  feeName: '',
  amount: 0
};

const FEE_TYPE_LABELS = {
  initiation: 'Initiation',
  activation: 'Activation',
  equipment: 'Equipment',
  locker_rental: 'Locker Rental',
  personal_training: 'Personal Training',
  late_fee: 'Late Fee',
  cancellation: 'Cancellation',
  other: 'Other'
};

const FEE_TYPE_COLORS = {
  initiation: 'primary',
  activation: 'secondary',
  equipment: 'info',
  locker_rental: 'warning',
  personal_training: 'success',
  late_fee: 'error',
  cancellation: 'error',
  other: 'default'
} as const;

const OneTimeFeesPage: React.FC = () => {
  const [fees, setFees] = useState<OneTimeFee[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [currentFeeId, setCurrentFeeId] = useState<number | null>(null);
  const [formData, setFormData] = useState<FeeFormData>(initialFormData);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);

  // Fetch all fees
  const fetchFees = async () => {
    setLoading(true);
    try {
      const response = await oneTimeFeeAPI.getAll();
      setFees(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching one-time fees:', err);
      setError('Failed to load one-time fees. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFees();
  }, []);

  // Handle form field changes for text inputs
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Handle select field changes
  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Handle numeric field changes
  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Allow empty string, or valid numbers
    if (value === '' || !isNaN(Number(value))) {
      setFormData({
        ...formData,
        amount: value === '' ? 0 : Number(value)
      });
    }
  };

  // Open dialog for creating a new fee
  const handleOpenCreateDialog = () => {
    setFormData(initialFormData);
    setEditMode(false);
    setCurrentFeeId(null);
    setDialogOpen(true);
  };

  // Open dialog for editing an existing fee
  const handleOpenEditDialog = (fee: OneTimeFee) => {
    setFormData({
      feeType: fee.feeType,
      feeName: fee.feeName,
      amount: fee.amount
    });
    setEditMode(true);
    setCurrentFeeId(fee.id);
    setDialogOpen(true);
  };

  // Close dialogs
  const handleCloseDialog = () => {
    setDialogOpen(false);
    setDeleteDialogOpen(false);
  };

  // Handle submit for creating or updating a fee
  const handleSubmit = async () => {
    try {
      // Validate form
      if (!formData.feeName.trim()) {
        setError('Fee name is required');
        return;
      }

      if (formData.amount <= 0) {
        setError('Amount must be greater than 0');
        return;
      }

      if (editMode && currentFeeId) {
        // Update existing fee
        await oneTimeFeeAPI.update(currentFeeId, formData);
        setSuccess('One-time fee updated successfully!');
      } else {
        // Create new fee
        await oneTimeFeeAPI.create(formData);
        setSuccess('One-time fee created successfully!');
      }

      // Close dialog and refresh data
      setDialogOpen(false);
      fetchFees();

      // Clear success message after 5 seconds
      setTimeout(() => {
        setSuccess(null);
      }, 5000);
    } catch (err) {
      console.error('Error saving one-time fee:', err);
      setError('Failed to save one-time fee. Please try again.');
    }
  };

  // Open delete confirmation dialog
  const handleOpenDeleteDialog = (id: number) => {
    setCurrentFeeId(id);
    setDeleteDialogOpen(true);
  };

  // Handle delete fee
  const handleDelete = async () => {
    if (!currentFeeId) return;

    try {
      await oneTimeFeeAPI.delete(currentFeeId);
      setSuccess('One-time fee deleted successfully!');
      setDeleteDialogOpen(false);
      fetchFees();

      // Clear success message after 5 seconds
      setTimeout(() => {
        setSuccess(null);
      }, 5000);
    } catch (err) {
      console.error('Error deleting one-time fee:', err);
      setError('Failed to delete one-time fee. Please try again.');
    }
  };

  // Helper function to format fee type for display
  const formatFeeType = (type: string) => {
    return FEE_TYPE_LABELS[type as keyof typeof FEE_TYPE_LABELS] || type;
  };

  // Helper function to get chip color for fee type
  const getFeeTypeColor = (type: string) => {
    return FEE_TYPE_COLORS[type as keyof typeof FEE_TYPE_COLORS] || 'default';
  };

  // Helper function to safely format amount
  const formatAmount = (amount: any): string => {
    if (amount === null || amount === undefined) {
      return '$0.00';
    }
    
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    
    if (isNaN(numAmount)) {
      return '$0.00';
    }
    
    return `$${numAmount.toFixed(2)}`;
  };

  return (
    <ErrorBoundary 
      fallbackTitle="One-Time Fees Page Error"
      fallbackMessage="There was an error loading the one-time fees page. This might be due to corrupted data or a temporary issue."
    >
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Breadcrumb navigation */}
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        aria-label="breadcrumb"
        sx={{ mb: 2 }}
      >
        <RouterLink to="/" style={{ textDecoration: 'none', color: 'inherit' }}>
          <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          Dashboard
        </RouterLink>
        <Typography color="text.primary">One-Time Fees</Typography>
      </Breadcrumbs>

      {/* Page header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          One-Time Fees
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenCreateDialog}
        >
          Create Fee
        </Button>
      </Box>

      {/* Alerts for error and success messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Main content */}
      <Paper sx={{ p: 3, mb: 4 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : fees.length === 0 ? (
          <Typography align="center" sx={{ p: 2 }}>
            No one-time fees found. Click the 'Create Fee Template' button to add one.
          </Typography>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Fee Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {fees.map((fee) => (
                  <TableRow key={fee.id}>
                    <TableCell>{fee.feeName}</TableCell>
                    <TableCell>
                      <Chip
                        label={formatFeeType(fee.feeType)}
                        color={getFeeTypeColor(fee.feeType)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{formatAmount(fee.amount)}</TableCell>
                    <TableCell>
                      {fee.createdAt ? new Date(fee.createdAt).toLocaleDateString() : '-'}
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        startIcon={<EditIcon />}
                        size="small"
                        sx={{ mr: 1 }}
                        onClick={() => handleOpenEditDialog(fee)}
                      >
                        Edit
                      </Button>
                      <Button
                        startIcon={<DeleteIcon />}
                        color="error"
                        size="small"
                        onClick={() => handleOpenDeleteDialog(fee.id)}
                      >
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} disableEscapeKeyDown={true} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <AttachMoneyIcon sx={{ mr: 1 }} />
            {editMode ? 'Edit One-Time Fee' : 'Create One-Time Fee Template'}
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {editMode
              ? 'Update the fee template details below.'
              : 'Create a fee template that can be assigned to members later.'}
          </DialogContentText>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <TextField
                name="feeName"
                label="Fee Name"
                value={formData.feeName}
                onChange={handleChange}
                fullWidth
                required
                margin="dense"
                placeholder="e.g., Monthly Gym Membership, Equipment Rental"
              />
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth margin="dense">
                <InputLabel id="fee-type-label">Fee Type</InputLabel>
                <Select
                  labelId="fee-type-label"
                  id="fee-type"
                  name="feeType"
                  value={formData.feeType}
                  label="Fee Type"
                  onChange={handleSelectChange}
                >
                  {Object.entries(FEE_TYPE_LABELS).map(([value, label]) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                name="amount"
                label="Amount"
                value={formData.amount}
                onChange={handleAmountChange}
                fullWidth
                required
                margin="dense"
                type="number"
                inputProps={{ min: 0, step: 0.01 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                }}
                helperText="Enter the fee amount in dollars"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {editMode ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        disableEscapeKeyDown={true}
        aria-labelledby="alert-dialog-title"
        aria-describedBy="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this one-time fee template? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleDelete} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
    </ErrorBoundary>
  );
};

export default OneTimeFeesPage;
